import { Container } from "react-bootstrap";
import { useIntegrationSearch } from "./hooks/useIntegrationSearch";
import {
  BlueprintSearchResultCard,
  IntegrationsDashboardGrid,
  IntegrationsLogoRail,
  IntegrationsSearchHero,
} from "./components";
import { LOGO_SEED, LogoSampleEntry } from "./utils/logoSeed";

// Per side. Round-robin rather than contiguous slices, so alphabetically adjacent logos land in
// different columns instead of each column being one alphabetical block.
const COLUMNS_PER_SIDE = 3;

const chunkIntoColumns = (
  integrations: LogoSampleEntry[],
  columnCount: number
): LogoSampleEntry[][] => {
  const columns: LogoSampleEntry[][] = Array.from({ length: columnCount }, () => []);
  integrations.forEach((integration, index) => columns[index % columnCount].push(integration));
  return columns;
};

const IntegrationsDashboard = () => {
  // hooks
  const {
    searchTerm,
    setSearchTerm,
    results,
    isLoading: isSearching,
    resolved,
    isNotFound,
    isIdentifierSearch,
  } = useIntegrationSearch();

  // derived state
  const hasSearchTerm = searchTerm.trim().length > 0;
  // Two halves, split into columns each, so the left and right sides don't mirror each other.
  const logoMidpoint = Math.ceil(LOGO_SEED.length / 2);
  const leftRailColumns = chunkIntoColumns(LOGO_SEED.slice(0, logoMidpoint), COLUMNS_PER_SIDE);
  const rightRailColumns = chunkIntoColumns(LOGO_SEED.slice(logoMidpoint), COLUMNS_PER_SIDE);

  // render
  const renderSearchResults = () => {
    if (isIdentifierSearch) {
      return (
        <BlueprintSearchResultCard
          resolved={resolved}
          isNotFound={isNotFound}
          isLoading={isSearching}
        />
      );
    }

    return <IntegrationsDashboardGrid integrations={results} isLoading={isSearching} />;
  };

  return (
    <>
      <IntegrationsLogoRail columns={leftRailColumns} side="left" />
      <IntegrationsLogoRail columns={rightRailColumns} side="right" />

      <Container fluid className="relative z-10 flex flex-col items-center px-8 pb-24 pt-32">
        <IntegrationsSearchHero searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

        {hasSearchTerm && (
          // Capped and centered so a wide result set (or its loading skeleton) stays clear of
          // the side rails instead of spanning the full container width underneath them.
          <div className="mt-12 flex w-full max-w-4xl flex-col items-center">
            {renderSearchResults()}
          </div>
        )}
      </Container>
    </>
  );
};

export default IntegrationsDashboard;
